import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { chunk } from 'lodash'
import { findResourceOfType, workflows } from '@opallabs/api/v2'
import { moments } from '@opallabs/api/v3'
import { type OpalApiResponseDocument } from '../../../schema'
import { globalQueryKeys } from '../../queryKeys'

type WorkflowDocument = OpalApiResponseDocument<'ReadWorkflowsV2', 200>

export type WorkflowResource = WorkflowDocument['data'][number]

/** One moment's approval workflow, named by the uuid v3 and the BFFs use. */
export type MomentWorkflow = Readonly<{
  momentId: string
  workflow: WorkflowResource
}>

/**
 * Long enough to be worth batching, short enough that the filter list stays
 * inside the ~2,000 character limit a URL can be relied on to carry.
 */
const CHUNK_SIZE = 100

/**
 * How many of the chunks are in the air at once. A window of a thousand
 * moments is ten chunks per leg, and letting all of them go at once takes the
 * browser's whole connection pool from the grid that a person is looking at.
 */
const MAX_IN_FLIGHT = 5

/**
 * Runs `read` over the chunks, never more than `MAX_IN_FLIGHT` at a time.
 *
 * The first chunk to fail fails the whole read, so the chunks nobody has
 * started are left alone rather than asked for an answer that is thrown away.
 */
const readChunks = async <TChunk, TResult>(
  chunks: readonly TChunk[],
  read: (chunk: TChunk) => Promise<TResult>
): Promise<TResult[]> => {
  const results: TResult[] = []
  let next = 0
  let hasFailed = false

  const worker = async () => {
    for (let index = next++; index < chunks.length; index = next++) {
      if (hasFailed) return
      try {
        results[index] = await read(chunks[index])
      } catch (error) {
        hasFailed = true
        throw error
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(MAX_IN_FLIGHT, chunks.length) }, worker)
  )

  return results
}

/**
 * The legacy id of each moment, against the uuid it was asked for by.
 *
 * `/workflows/v2` knows moments by their v2 primary key alone, so a caller
 * holding uuids — which is everything written since v3 — has to translate
 * first. The sparse fieldset keeps that translation to the one attribute it is
 * for.
 */
const momentUuidsByLegacyId = async (
  momentIds: readonly string[]
): Promise<Map<string, string>> => {
  const documents = await readChunks(chunk([...momentIds], CHUNK_SIZE), (ids) =>
    moments.readCollection({
      query: {
        fields: { moment: 'legacy_id' },
        filter: { id: ids },
        page: { limit: CHUNK_SIZE },
      },
    })
  )

  return new Map(
    documents
      .flatMap((document) => document.data ?? [])
      .map((moment) => [moment.attributes.legacy_id, moment.id])
  )
}

const workflowsForLegacyIds = async (
  legacyIds: readonly string[]
): Promise<WorkflowDocument[]> =>
  readChunks(chunk([...legacyIds], CHUNK_SIZE), (ids) =>
    workflows.readCollection({
      query: {
        expose: { workflow: { meta: null } },
        filter: { 'context.moment_id': ids.map(Number) },
        include: ['context'],
        page: { limit: CHUNK_SIZE },
      },
    })
  )

const readMomentWorkflows = async (
  momentIds: readonly string[],
  legacyIds: ReadonlyMap<string, string> | undefined
): Promise<MomentWorkflow[]> => {
  const unknownIds = momentIds.filter((id) => !legacyIds?.has(id))
  const uuidByLegacyId =
    unknownIds.length === 0
      ? new Map<string, string>()
      : await momentUuidsByLegacyId(unknownIds)
  momentIds.forEach((id) => {
    const legacyId = legacyIds?.get(id)
    if (legacyId !== undefined) uuidByLegacyId.set(legacyId, id)
  })
  if (uuidByLegacyId.size === 0) return []

  const documents = await workflowsForLegacyIds([...uuidByLegacyId.keys()])

  return documents.flatMap((document) =>
    document.data.flatMap((workflow) => {
      const context = document.included?.find(
        findResourceOfType(
          'context',
          'V2',
          workflow.relationships.context?.data.id
        )
      )
      const legacyId = context?.relationships.moment?.data.id
      const momentId =
        legacyId === undefined
          ? undefined
          : uuidByLegacyId.get(String(legacyId))

      // A workflow whose moment is not one we asked about belongs to nobody
      // here, and a workflow over something other than a moment has no moment
      // at all.
      return momentId === undefined ? [] : [{ momentId, workflow }]
    })
  )
}

/**
 * The approval workflow running over each of these moments, for the moments
 * that have one. A moment with no workflow is simply absent from the answer.
 *
 * Two requests stand behind this, because no endpoint joins them: moments are
 * asked for their legacy ids, then the workflows are asked for by those ids.
 * Both are batched a hundred moments at a time, five batches at a time. A
 * caller that already holds the legacy ids hands them over in `legacyIds`, and
 * only the moments missing from it are asked for theirs.
 */
export const useReadMomentWorkflows = <TSelected = MomentWorkflow[]>({
  enabled = true,
  legacyIds,
  momentIds,
  scope,
  select,
}: {
  /** Set false to hold the fetch back until the summaries are wanted. */
  enabled?: boolean
  /** Moment uuids. */
  momentIds: readonly string[]
  /**
   * The legacy id of each moment, by uuid, for the moments the caller already
   * knows it for. The rest are looked up.
   */
  legacyIds?: ReadonlyMap<string, string>
  /**
   * What this list of moments is — a month window, say. It is the whole cache
   * key, so two different sets of moments must never be read under one scope,
   * and whoever changes what the scope holds has to invalidate it.
   */
  scope: string
  select?: (data: MomentWorkflow[]) => TSelected
}): UseQueryResult<TSelected> =>
  useQuery({
    enabled: enabled && momentIds.length > 0,
    queryFn: () => readMomentWorkflows(momentIds, legacyIds),
    // The scope is the key on purpose: see the `scope` doc above.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: globalQueryKeys.workflowsByMomentScope(scope),
    select,
  })
